import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate, useSearchParams } from "react-router-dom";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { ProjectSelectionProvider } from "@/contexts/ProjectSelectionContext";
import ProtectedRoute from "./components/auth/ProtectedRoute";
import RoleProtectedRoute from "./components/auth/RoleProtectedRoute";
import { usePostHog } from "./hooks/usePostHog";
import { useEffect, useState } from "react";
import ErrorBoundary from "./components/ErrorBoundary";
import { AppLayout } from "@/components/AppLayout";

// Public pages
import ApiDocs from "./pages/ApiDocs";
import PublicApiDocs from "./pages/PublicApiDocs";

// Auth pages
import SignIn from "./pages/SignIn";
import SignUp from "./pages/auth/SignUp";
import ForgotPassword from "./pages/auth/ForgotPassword";
import ResetPasswordPage from "./pages/auth/ResetPasswordPage";
import AuthCallback from "./pages/auth/AuthCallback";
import GoogleSignUpComplete from "./pages/auth/GoogleSignUpComplete";
import EmailVerificationPending from "./pages/auth/EmailVerificationPending";
import VerifyEmail from "./pages/auth/VerifyEmail";
import InvitePage from "./pages/InvitePage";

// Sophie Auth pages
import SophieForgotPassword from "./pages/sophie/ForgotPassword";
import SophieResetPassword from "./pages/sophie/ResetPassword";
import SophieEmailVerificationPending from "./pages/sophie/EmailVerificationPending";
import SophieVerifyEmail from "./pages/sophie/VerifyEmail";

// Protected pages
import Dashboard from "./pages/Dashboard";
import GettingStarted from "./pages/GettingStarted";
import MemoryEntries from "./pages/MemoryEntries";
import ExportDelete from "./pages/ExportDelete";
import AuditLogs from "./pages/AuditLogs";
import ApiKeys from "./pages/ApiKeys";
import MemorySourcesPage from "./pages/MemorySourcesPage";
import TeamManagement from "./pages/TeamManagement";
import ContextPlayground from "./pages/ContextPlayground";
import Playground from "./pages/Playground";
import Settings from "./pages/Settings";
import Projects from "./pages/Projects";
import StatusPage from "./pages/StatusPage";
import Help from "./pages/Help";
import NotFound from "./pages/NotFound";
import Billing from "./pages/Billing";
import UsageStats from "./pages/UsageStats";
import Webhooks from "./pages/Webhooks";
import { teamService } from "./services/teamService";
import { businessService } from "./services/businessService";
import { isSophieHost } from "./lib/config";
import Chatbot from "./pages/Chatbot";
import ChatbotHistory from "./pages/ChatbotHistory";
import Demo from "./pages/Demo";

import SophieDashboard from "./pages/sophie/Dashboard";
import Login from "./pages/sophie/Login";
import Onboarding from "./pages/sophie/Onboarding";
import YourBusiness from "./pages/sophie/YourBusiness";
import KnowledgeDocs from "./pages/sophie/KnowledgeDocs";
import ChatHistory from "./pages/sophie/ChatHistory";
import SophieBilling from "./pages/sophie/Billing";
import Team from "./pages/sophie/Team";
import Support from "./pages/sophie/Support";
import Integrations from "./pages/sophie/Integrations";
import Notification from "./pages/sophie/Notification";
import SophieSettings from "./pages/sophie/Settings";
import VoiceSettings from "./pages/sophie/VoiceSettings";
import SophieNotFound from "./pages/sophie/NotFound";
import SophieInvitePage from "./pages/sophie/InvitePage";
import SuperAdmin from "./pages/sophie/SuperAdmin";

const queryClient = new QueryClient();

// PostHog tracking component
const PostHogTracker = () => {
  usePostHog();
  return null;
};

// Subscription parameter handler component
const SubscriptionHandler = ({ children }: { children: React.ReactNode }) => {
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const subscription = urlParams.get('subscription');
    
    console.log('SubscriptionHandler - checking URL params:', {
      subscription,
      isValid: subscription && ['1', '2', '3', '4', '5'].includes(subscription),
      currentLocation: window.location.href,
      existingStorage: localStorage.getItem('selected-subscription')
    });
    
    if (subscription && ['1', '2', '3', '4', '5'].includes(subscription)) {
      console.log('SubscriptionHandler - storing subscription parameter:', subscription);
      localStorage.setItem('selected-subscription', subscription);
      
      // Verify storage
      const stored = localStorage.getItem('selected-subscription');
      console.log('SubscriptionHandler - verification:', {
        stored,
        matches: stored === subscription
      });
    }
  }, []);

  return <>{children}</>;
};

// Root component that handles subscription parameters
const RootHandler = () => {
  const { isAuthenticated, isLoading, user } = useAuth();
  const [profileLoading, setProfileLoading] = useState(true);
  const [onboardingComplete, setOnboardingComplete] = useState(false);
  const [teamSubscription, setTeamSubscription] = useState<number | null>(null);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const subscription = urlParams.get('subscription');
    
    console.log('RootHandler - checking URL params:', {
      subscription,
      isValid: subscription && ['1', '2', '3', '4', '5'].includes(subscription),
      currentLocation: window.location.href,
      existingStorage: localStorage.getItem('selected-subscription')
    });
    
    if (subscription && ['1', '2', '3', '4', '5'].includes(subscription)) {
      console.log('RootHandler - storing subscription parameter:', subscription);
      localStorage.setItem('selected-subscription', subscription);
      
      // Verify storage
      const stored = localStorage.getItem('selected-subscription');
      console.log('RootHandler - verification:', {
        stored,
        matches: stored === subscription
      });
    }
  }, []);

  useEffect(() => {
    const checkOnboardingStatus = async () => {
      if (isAuthenticated && !isLoading) {
        const isSophie = user?.isSophie == true || isSophieHost();
        if (isSophie) {
          try {
            const teamData = await teamService.getTeam();
            setTeamSubscription(teamData.subscription);
          } catch (error) {
            console.warn('Failed to load team subscription:', error);
          }

          // Save pending business data from onboarding if present
          const pendingRaw = localStorage.getItem('sophie_pending_business_data');
          if (pendingRaw) {
            try {
              const pending = JSON.parse(pendingRaw);
              if (pending.business) {
                const b = pending.business;
                await businessService.updateBusinessInfo({
                  businessName: b.name,
                  businessCategory: b.categories?.[0] || "",
                  address: b.address,
                  businessPhone: b.phone,
                  website: b.website,
                  about: b.rating ? `Google Rating: ${b.rating} (${b.reviewCount} reviews)` : undefined,
                });
                if (b.hours) {
                  await businessService.updateBusinessHours(b.hours);
                }
              }
              localStorage.removeItem('sophie_pending_business_data');
              localStorage.removeItem('isSophieSignup');
            } catch (error) {
              console.warn('Failed to save pending business data:', error);
            }
          }

          setOnboardingComplete(true);
          setProfileLoading(false);
          return;
        }
        try {
          const profileData = await teamService.getUserProfile();
          const saved = profileData.onboardingStep;
          const steps = saved ? JSON.parse(saved) : [];
          setOnboardingComplete(steps.length >= 5);
        } catch (error) {
          console.warn('Failed to load onboarding status:', error);
          setOnboardingComplete(false);
        } finally {
          setProfileLoading(false);
        }
      } else if (!isAuthenticated && !isLoading) {
        setProfileLoading(false);
      }
    };
    
    checkOnboardingStatus();
  }, [isAuthenticated, isLoading]);

  console.log('RootHandler - authentication check:', {
    isAuthenticated,
    isLoading,
    userRole: user?.teamRole,
    currentStorage: localStorage.getItem('selected-subscription')
  });
  
  // Wait for authentication state to be determined
  if (isLoading || (isAuthenticated && profileLoading)) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-background">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }
  const isSophie = user?.isSophie == true || isSophieHost();
  console.log('RootHandler - isSophie:'+isSophie)
  if (isAuthenticated) {
    if (isSophie) {
      if (teamSubscription === 1) {
        return <Navigate to="/billing" replace />;
      }
      return <Navigate to="/dashboard" replace />;
    }
    const urlParams = new URLSearchParams(window.location.search);
    const subscription = urlParams.get('subscription');
    
    if (subscription && ['1', '2', '3', '4', '5'].includes(subscription)) {
      // User is logged in and has subscription parameter, redirect to billing with subscription
      console.log('Root - authenticated user with subscription, redirecting to billing');
      return <Navigate to={`/billing?subscription=${subscription}`} replace />;
    } else {
      if (!onboardingComplete) {
        console.log('Root - authenticated user needs onboarding, redirecting to getting started');
        return <Navigate to="/getting-started" replace />;
      } else {
        // User is logged in but no subscription, redirect to dashboard
        console.log('Root - authenticated user without subscription, redirecting to dashboard');
        return <Navigate to="/dashboard" replace />;
      }
    }
  } else {
    if (isSophie) {
      return <Navigate to="/login" replace />;
    }
    // User is not authenticated
    const urlParams = new URLSearchParams(window.location.search);
    const subscription = urlParams.get('subscription');
    
    if (subscription && ['1', '2', '3', '4', '5'].includes(subscription)) {
      // User is not logged in but has subscription parameter, redirect to signin with subscription
      console.log('Root - unauthenticated user with subscription, redirecting to signin');
      return <Navigate to={`/signup?subscription=${subscription}`} replace />;
    } else {
      // User is not authenticated and no subscription, redirect to signin
      console.log('Root - unauthenticated user without subscription, redirecting to signin');
      return <Navigate to="/signin" replace />;
    }
  }
};

const DashboardRoute = () => {
  const { user } = useAuth();
  const isSophie = user?.isSophie == true || isSophieHost();

  if (isSophie) {
    return <AppLayout><SophieDashboard /></AppLayout>;
  }
  return <Dashboard />;
};

const BillingRoute = () => {
  const { user } = useAuth();
  const isSophie = user?.isSophie == true || isSophieHost();

  if (isSophie) {
    return <AppLayout><SophieBilling /></AppLayout>;
  }
  return <Billing />;
};

const SettingsRoute = () => {
  const { user } = useAuth();
  const isSophie = user?.isSophie == true || isSophieHost();

  if (isSophie) {
    return <AppLayout><SophieSettings /></AppLayout>;
  }
  return <Settings />;
};

const NotFoundRoute = () => {
  const { user } = useAuth();
  const isSophie = user?.isSophie == true || isSophieHost();

  if (isSophie) {
    return <AppLayout><SophieNotFound /></AppLayout>;
  }
  return <NotFound />;
};

const SignInRoute = () => {
  const isSophie = isSophieHost();

  if (isSophie) {
    return <Login />;
  }
  return <SignIn />;
};

const ForgotPasswordRoute = () => {
  const isSophie = isSophieHost();

  if (isSophie) {
    return <SophieForgotPassword />;
  }
  return <ForgotPassword />;
};

const ResetPasswordRoute = () => {
  const isSophie = isSophieHost();

  if (isSophie) {
    return <SophieResetPassword />;
  }
  return <ResetPasswordPage />;
};

const EmailVerificationPendingRoute = () => {
  const isSophie = isSophieHost();

  if (isSophie) {
    return <SophieEmailVerificationPending />;
  }
  return <EmailVerificationPending />;
};

const VerifyEmailRoute = () => {
  const isSophie = isSophieHost();

  if (isSophie) {
    return <SophieVerifyEmail />;
  }
  return <VerifyEmail />;
};

const InviteRoute = () => {
  const isSophie = isSophieHost();

  if (isSophie) {
    return <SophieInvitePage />;
  }
  return <InvitePage />;
};

const App = () => (
  <ErrorBoundary>
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <PostHogTracker />
          <AuthProvider>
            <ProjectSelectionProvider>
              <SubscriptionHandler>
                <Routes>
              {/* Redirect root to signin */}
              <Route path="/" element={<RootHandler />} />
              
              {/* Public API documentation */}
              <Route path="/api-docs" element={<ApiDocs />} />
              <Route path="/docs" element={<PublicApiDocs />} />
              

              {/* Sophie routes */}
              <Route path="/login" element={<Login />} />
              <Route path="/onboarding" element={<Onboarding />} />

              {/* Auth routes */}
              <Route path="/signin" element={<SignInRoute />} />
              <Route path="/signup" element={<SignUp />} />
              <Route path="/forgot-password" element={<ForgotPasswordRoute />} />
              <Route path="/auth/callback" element={<AuthCallback />} />
              <Route path="/auth/google-signup-complete" element={<GoogleSignUpComplete />} />
              <Route path="/auth/email-verification-pending" element={<EmailVerificationPendingRoute />} />
              <Route path="/auth/verify-email" element={<VerifyEmailRoute />} />
              
              {/* Public invite route */}
              <Route path="/invite/:inviteId" element={<InviteRoute />} />
              
              {/* Public password reset route */}
              <Route path="/reset-password/:resetId" element={<ResetPasswordRoute />} />
              
              {/* Protected routes */}
              <Route path="/getting-started" element={
                <ProtectedRoute>
                  <GettingStarted />
                </ProtectedRoute>
              } />
              <Route path="/dashboard" element={
                <ProtectedRoute>
                  <DashboardRoute />
                </ProtectedRoute>
              } />
              <Route path="/memory-entries" element={
                <ProtectedRoute>
                  <MemoryEntries />
                </ProtectedRoute>
              } />
              <Route path="/export-delete" element={
                <RoleProtectedRoute requiredRole="admin">
                  <ExportDelete />
                </RoleProtectedRoute>
              } />
              <Route path="/audit-logs" element={
                <RoleProtectedRoute requiredRole="admin">
                  <AuditLogs />
                </RoleProtectedRoute>
              } />
              <Route path="/api-keys" element={
                <ProtectedRoute>
                  <ApiKeys />
                </ProtectedRoute>
              } />
              <Route path="/chatbot" element={
                <ProtectedRoute>
                  <Chatbot />
                </ProtectedRoute>
              } />
              <Route path="/demo" element={
                <RoleProtectedRoute requiredRole="super_admin">
                  <Demo />
                </RoleProtectedRoute>
              } />
              <Route path="/superadmin" element={
                <RoleProtectedRoute requiredRole="super_admin">
                  <AppLayout><SuperAdmin /></AppLayout>
                </RoleProtectedRoute>
              } />
              <Route path="/chatbot-history" element={
                <ProtectedRoute>
                  <ChatbotHistory />
                </ProtectedRoute>
              } />
              <Route path="/memory-sources" element={
                <ProtectedRoute>
                  <MemorySourcesPage />
                </ProtectedRoute>
              } />
              <Route path="/team-management" element={
                <ProtectedRoute>
                  <TeamManagement />
                </ProtectedRoute>
              } />
              <Route path="/context" element={
                <ProtectedRoute>
                  <ContextPlayground />
                </ProtectedRoute>
              } />
              <Route path="/playground" element={
                <ProtectedRoute>
                  <Playground />
                </ProtectedRoute>
              } />
              <Route path="/projects" element={
                <ProtectedRoute>
                  <Projects />
                </ProtectedRoute>
              } />
              <Route path="/settings" element={
                <ProtectedRoute>
                  <SettingsRoute />
                </ProtectedRoute>
              } />
              <Route path="/status" element={
                <ProtectedRoute>
                  <StatusPage />
                </ProtectedRoute>
              } />
              <Route path="/help" element={
                <ProtectedRoute>
                  <Help />
                </ProtectedRoute>
              } />
              <Route path="/billing" element={
                <RoleProtectedRoute requiredRole="admin" redirectTo="/dashboard">
                  <BillingRoute />
                </RoleProtectedRoute>
              } />
              <Route path="/usage-stats" element={
                <ProtectedRoute>
                  <UsageStats />
                </ProtectedRoute>
              } />
              <Route path="/webhooks" element={
                <ProtectedRoute>
                  <Webhooks />
                </ProtectedRoute>
              } />
              
              <Route path="/business" element={
                <RoleProtectedRoute requiredRole="admin" redirectTo="/dashboard">
                  <AppLayout><YourBusiness /></AppLayout>
                </RoleProtectedRoute>
              } />
              <Route path="/voice-settings" element={
                <RoleProtectedRoute requiredRole="admin" redirectTo="/dashboard">
                  <AppLayout><VoiceSettings /></AppLayout>
                </RoleProtectedRoute>
              } />
              <Route path="/knowledge" element={
                <RoleProtectedRoute requiredRole="admin" redirectTo="/dashboard">
                  <AppLayout><KnowledgeDocs /></AppLayout>
                </RoleProtectedRoute>
              } />
              <Route path="/chat-history" element={
                <ProtectedRoute>
                  <AppLayout><ChatHistory /></AppLayout>
                </ProtectedRoute>
              } />
              <Route path="/team" element={
                <RoleProtectedRoute requiredRole="admin" redirectTo="/dashboard">
                  <AppLayout><Team /></AppLayout>
                </RoleProtectedRoute>
              } />
              <Route path="/support" element={
                <ProtectedRoute>
                  <AppLayout><Support /></AppLayout>
                </ProtectedRoute>
              } />
              <Route path="/integrations" element={
                <RoleProtectedRoute requiredRole="admin" redirectTo="/dashboard">
                  <AppLayout><Integrations /></AppLayout>
                </RoleProtectedRoute>
              } />
              <Route path="/notifications" element={
                <ProtectedRoute>
                  <AppLayout><Notification /></AppLayout>
                </ProtectedRoute>
              } />
              {/* 404 page */}
              <Route path="*" element={<NotFoundRoute />} />
                </Routes>
              </SubscriptionHandler>
            </ProjectSelectionProvider>
          </AuthProvider>
        </BrowserRouter>
      </TooltipProvider>
    </QueryClientProvider>
  </ErrorBoundary>
);

export default App;
